/* ============================================================================
 *  API adapters — Split invite (invitee side)
 * ----------------------------------------------------------------------------
 *  Normalise the SplitInvitationPublicDTO (Modules/Community) into the shape the
 *  §2 private invite page renders. The DTO is fetched via the authenticated
 *  `api` helper (the route is private — the user is always signed in), NOT the
 *  mentoring `publicGet`. Field names are read defensively so a small backend
 *  rename doesn't blank the landing.
 * ========================================================================== */

import { SPLIT_INVITE_STATE } from "../enums/splitInviteStates";

/**
 * SplitInvitationPublicDTO (flat-ish) → the { community, owner, … } shape the
 * invite card renders.
 *   { id, status, invited_email, percentile, notes, expires_at,
 *     community: { name, banner, members_count },
 *     owner:     { name, avatar } }
 */
export function adaptSplitInvitation(raw) {
  if (!raw) return null;

  const community = raw.community ?? {};
  const owner = raw.owner ?? {};

  return {
    id: raw.id ?? null,
    status: raw.status ?? "pending",
    invited_email: raw.invited_email ?? raw.email ?? "",
    percentile: raw.percentile ?? null,
    notes: raw.notes ?? null,
    expires_at: raw.expires_at ?? null,
    community: {
      id: community.id ?? null,
      name: community.name ?? null,
      banner: community.banner ?? community.banner_url ?? null,
      members_count:
        community.members_count ?? community.members ?? community.membersCount ?? null,
    },
    owner: {
      name: owner.name ?? owner.username ?? null,
      avatar: owner.avatar ?? owner.avatar_url ?? null,
    },
  };
}

/**
 * Map an `api`-style error response to a terminal SPLIT_INVITE_STATE. 404 =
 * unknown token; 403 = the token belongs to a different account (email
 * boundary); a 422 carries the business reason (expired vs. already used) in
 * its message; anything else is a generic error. Mirrors mapInviteError.
 */
export function mapSplitInviteError(response) {
  const status = response?.status;
  const message = response?.data?.message || "";

  if (status === 404) return SPLIT_INVITE_STATE.NOT_FOUND;
  if (status === 403) return SPLIT_INVITE_STATE.WRONG_EMAIL;
  if (status === 422) {
    if (/expirad/i.test(message)) return SPLIT_INVITE_STATE.EXPIRED;
    return SPLIT_INVITE_STATE.USED;
  }
  return SPLIT_INVITE_STATE.ERROR;
}
